/**
 * UI Evidence Map Registry
 *
 * Maps every UI field to its evidence configuration.
 * This is a compile-time guarantee that no metric can be displayed without an evidence path.
 *
 * @see WORKPLAN-LENDER-AUDIT-TRACEABILITY-MATRIX.md
 */

import { MetricKey, DisclosureLevel } from '../types/evidenceContract';
import { IncomeBasis } from '../types/financialStats';

// =============================================================================
// TYPES
// =============================================================================

export type SourceType =
  | 'PLAID_TRANSACTIONS'
  | 'PLAID_BALANCES'
  | 'PLAID_LIABILITIES'
  | 'BUREAU_CREDIT'
  | 'INTERNAL_DERIVED'
  | 'USER_ATTESTATION';  // VS-BUG-009: User-provided data

/**
 * VS-BUG-009: Drilldown Capability Level
 *
 * Defines what level of evidence detail is available for a metric.
 * This is dynamic based on the data source (e.g., income_basis_used).
 *
 * - FULL_DRILLDOWN: Can trace to individual transactions or source documents
 * - SUMMARY: Calculated from other metrics (no direct transaction link)
 * - ATTESTATION_ONLY: User-provided data without transaction/document evidence
 */
export type DrilldownCapability = 'FULL_DRILLDOWN' | 'SUMMARY' | 'ATTESTATION_ONLY';

export type DrilldownTab = 'summary' | 'breakdown' | 'calculation' | 'sources' | 'raw' | 'attestation';

export interface UiEvidenceConfig {
  metric_key: MetricKey;
  label: string;
  description: string;
  min_disclosure_level: DisclosureLevel;  // Minimum level to see this metric
  source_types: SourceType[];
  drilldown_tabs: DrilldownTab[];
  window_type: 'MONTH' | 'AS_OF' | 'ROLLING_6M' | 'ROLLING_12M' | 'ROLLING_24M';
  unit: 'USD' | 'PERCENT' | 'COUNT' | 'RATIO' | 'MONTHS';
  has_components: boolean;  // Can break down into categories/items
  has_transactions: boolean;  // Can drill into individual transactions
  /** VS-BUG-009: Default drilldown capability (may be overridden by income_basis_used) */
  default_drilldown_capability?: DrilldownCapability;
}

/**
 * VS-BUG-009: Get drilldown capability based on income basis
 *
 * Evidence level must be dynamic per basis:
 * - VERIFIED_GROSS → FULL_DRILLDOWN (payroll events / documents)
 * - DEPOSITED_NET → FULL_DRILLDOWN (transaction list)
 * - ATTESTED → ATTESTATION_ONLY (no fake drilldown)
 */
export function getDrilldownCapabilityForIncomeBasis(
  incomeBasis: IncomeBasis
): DrilldownCapability {
  switch (incomeBasis) {
    case 'VERIFIED_GROSS':
      return 'FULL_DRILLDOWN'; // Payroll events / documents
    case 'DEPOSITED_NET':
      return 'FULL_DRILLDOWN'; // Transaction list
    case 'ATTESTED':
      return 'ATTESTATION_ONLY'; // User-provided, no fake drilldown
    default:
      return 'SUMMARY';
  }
}

/**
 * VS-BUG-009: Get available drilldown tabs based on capability
 */
export function getDrilldownTabsForCapability(
  capability: DrilldownCapability
): DrilldownTab[] {
  switch (capability) {
    case 'FULL_DRILLDOWN':
      return ['summary', 'breakdown', 'calculation', 'sources'];
    case 'SUMMARY':
      return ['summary', 'calculation'];
    case 'ATTESTATION_ONLY':
      return ['summary', 'attestation']; // No fake drilldown, show attestation info
    default:
      return ['summary'];
  }
}

// =============================================================================
// REGISTRY
// =============================================================================

export const UI_EVIDENCE_MAP: Record<string, UiEvidenceConfig> = {
  // ---------------------------------------------------------------------------
  // CURRENT FINANCES SECTION (11 metrics)
  // Maps to algo_run_evidence.entity_name (CANONICAL_METRICS from backend)
  // ---------------------------------------------------------------------------

  // BUG-UI-029: Yearly Income - maps to gross_income_6m (annualized)
  // VS-BUG-001: Renamed "Gross Income" → "Deposited Income" for audit accuracy
  // Bank deposits are NOT gross income (pre-tax/pre-deduction) - they are net deposited amounts
  // Dashboard shows "Yearly Deposited Income: $144,000" - should open yearly metric
  'lender.current_finances.yearly_gross_income': {
    metric_key: 'gross_income_6m',  // Maps to algo_run_evidence.entity_name (6-month avg × 12)
    label: 'Annual Deposited Income',
    description: 'Annualized income based on 6-month average of bank deposits. Note: This is net deposited income, not gross (pre-tax) income.',
    min_disclosure_level: 'LENDER',
    source_types: ['PLAID_TRANSACTIONS'],
    drilldown_tabs: ['summary', 'breakdown', 'calculation', 'sources'],
    window_type: 'ROLLING_6M',
    unit: 'USD',
    has_components: true,
    has_transactions: true,
  },

  // VS-BUG-001: Renamed "Gross Income (Monthly)" → "Deposited Income (Monthly)"
  // BUG-UI-145: Renamed back to "Gross Income" to match table label in FINANCIAL_METRICS (constants.ts)
  // The table shows "Gross Income" so the modal title must match to avoid cognitive friction
  'lender.current_finances.gross_income': {
    metric_key: 'money_in_1m',  // Maps to algo_run_evidence.entity_name
    label: 'Gross Income',
    description: 'Sum of inflows classified as income (payroll/benefits) in the month. Note: This is net deposited income, not gross (pre-tax) income.',
    min_disclosure_level: 'LENDER',
    source_types: ['PLAID_TRANSACTIONS'],
    drilldown_tabs: ['summary', 'breakdown', 'calculation', 'sources'],
    window_type: 'MONTH',
    unit: 'USD',
    has_components: true,
    has_transactions: true,
  },

  'lender.current_finances.minimum_debt_payments': {
    metric_key: 'monthly_debt_payments',  // Maps to algo_run_evidence.entity_name
    label: 'Minimum Debt Payments',
    description: 'Sum of minimum required monthly payments across included liabilities',
    min_disclosure_level: 'LENDER',
    source_types: ['PLAID_LIABILITIES'],
    drilldown_tabs: ['summary', 'breakdown', 'calculation', 'sources'],
    window_type: 'MONTH',
    unit: 'USD',
    has_components: true,
    has_transactions: false,
  },

  'lender.current_finances.cash_on_hand': {
    metric_key: 'cash_on_hand',  // Maps to algo_run_evidence.entity_name
    label: 'Cash On Hand',
    description: 'Sum of balances across included depository accounts as-of snapshot time',
    min_disclosure_level: 'LENDER',
    source_types: ['PLAID_BALANCES'],
    drilldown_tabs: ['summary', 'breakdown', 'calculation', 'sources'],
    window_type: 'AS_OF',
    unit: 'USD',
    has_components: true,
    has_transactions: false,
  },

  'lender.current_finances.money_in': {
    metric_key: 'money_in_1m',  // Maps to algo_run_evidence.entity_name
    label: 'Money In',
    description: 'Sum of all inflows (credits) in month',
    min_disclosure_level: 'LENDER',
    source_types: ['PLAID_TRANSACTIONS'],
    drilldown_tabs: ['summary', 'breakdown', 'calculation', 'sources'],
    window_type: 'MONTH',
    unit: 'USD',
    has_components: true,
    has_transactions: true,
  },

  'lender.current_finances.money_leftover': {
    metric_key: 'money_leftover_1m',  // Maps to algo_run_evidence.entity_name
    label: 'Money Leftover',
    description: 'Net discretionary remainder: money_in - expenses - debt_payments',
    min_disclosure_level: 'LENDER',
    source_types: ['INTERNAL_DERIVED'],
    drilldown_tabs: ['summary', 'calculation'],
    window_type: 'MONTH',
    unit: 'USD',
    has_components: false,
    has_transactions: false,
  },

  'lender.current_finances.dti_ratio': {
    metric_key: 'dti_ratio',  // Maps to algo_run_evidence.entity_name
    label: 'Debt-to-Income Ratio',
    description: 'Minimum debt payments divided by gross income',
    min_disclosure_level: 'LENDER',
    source_types: ['INTERNAL_DERIVED'],
    drilldown_tabs: ['summary', 'calculation'],
    window_type: 'MONTH',
    unit: 'PERCENT',
    has_components: false,
    has_transactions: false,
  },

  'lender.current_finances.monthly_expenses': {
    metric_key: 'money_out_1m',  // Maps to algo_run_evidence.entity_name
    label: 'Monthly Expenses',
    description: 'Sum of outflows classified as expenses (excluding transfers, debt payments)',
    min_disclosure_level: 'LENDER',
    source_types: ['PLAID_TRANSACTIONS'],
    drilldown_tabs: ['summary', 'breakdown', 'calculation', 'sources'],
    window_type: 'MONTH',
    unit: 'USD',
    has_components: true,
    has_transactions: true,
  },

  'lender.current_finances.net_savings': {
    metric_key: 'net_savings_1m',  // Maps to algo_run_evidence.entity_name
    label: 'Net Savings',
    description: 'Positive monthly remainder (floored at 0)',
    min_disclosure_level: 'LENDER',
    source_types: ['INTERNAL_DERIVED'],
    drilldown_tabs: ['summary', 'calculation'],
    window_type: 'MONTH',
    unit: 'USD',
    has_components: false,
    has_transactions: false,
  },
  
  'lender.current_finances.outstanding_debt': {
    metric_key: 'total_debt',  // Maps to CANONICAL_METRICS.TOTAL_DEBT
    label: 'Outstanding Debt',
    description: 'Total current balances across included liabilities',
    min_disclosure_level: 'LENDER',
    source_types: ['PLAID_LIABILITIES', 'BUREAU_CREDIT'],
    drilldown_tabs: ['summary', 'breakdown', 'calculation', 'sources'],
    window_type: 'AS_OF',
    unit: 'USD',
    has_components: true,
    has_transactions: false,
  },

  // BUG-UI-131 FIX: Late payments data is stored under payment_history_factor entity
  // The API returns evidence for payment_history_factor with number_of_late_payments in intermediate_metrics
  // Dashboard "Late Payments" KPI should query payment_history_factor to show the actual data
  'lender.current_finances.late_payments': {
    metric_key: 'payment_history_factor',  // BUG-UI-131: Query payment_history_factor (where data actually lives)
    label: 'Late Payments',  // Keep display label as "Late Payments"
    description: 'Count of delinquency events in prior 12 months (30/60/90+)',
    min_disclosure_level: 'LENDER',
    source_types: ['BUREAU_CREDIT', 'PLAID_LIABILITIES'],
    drilldown_tabs: ['summary', 'breakdown', 'calculation'],  // Use same tabs as payment_history_factor
    window_type: 'ROLLING_12M',
    unit: 'COUNT',
    has_components: true,
    has_transactions: false,
  },

  'lender.current_finances.credit_score': {
    metric_key: 'credit_score',  // Maps to simplified key
    label: 'Credit Score',
    description: 'Score from bureau pull (score model and version tracked)',
    min_disclosure_level: 'LENDER',
    source_types: ['BUREAU_CREDIT'],
    drilldown_tabs: ['summary', 'sources'],
    window_type: 'AS_OF',
    unit: 'COUNT',
    has_components: false,
    has_transactions: false,
  },

  // ---------------------------------------------------------------------------
  // FACTOR SCORES (6 metrics) - Used in Factor Contribution Charts
  // ---------------------------------------------------------------------------

  'lender.factors.income_stability': {
    metric_key: 'factor_income_stability',
    label: 'Income Stability',
    description: 'Factor score based on income consistency and growth',
    min_disclosure_level: 'LENDER',
    source_types: ['INTERNAL_DERIVED', 'PLAID_TRANSACTIONS'],
    drilldown_tabs: ['summary', 'breakdown', 'calculation'],
    window_type: 'ROLLING_6M',
    unit: 'COUNT',
    has_components: true,
    has_transactions: false,
  },

  'lender.factors.expense_management': {
    metric_key: 'factor_expense_management',
    label: 'Expense Management',
    description: 'Factor score based on spending patterns and budget adherence',
    min_disclosure_level: 'LENDER',
    source_types: ['INTERNAL_DERIVED', 'PLAID_TRANSACTIONS'],
    drilldown_tabs: ['summary', 'breakdown', 'calculation'],
    window_type: 'ROLLING_6M',
    unit: 'COUNT',
    has_components: true,
    has_transactions: false,
  },

  'lender.factors.savings_behavior': {
    metric_key: 'factor_savings_behavior',
    label: 'Savings Behavior',
    description: 'Factor score based on savings rate and consistency',
    min_disclosure_level: 'LENDER',
    source_types: ['INTERNAL_DERIVED', 'PLAID_TRANSACTIONS', 'PLAID_BALANCES'],
    drilldown_tabs: ['summary', 'breakdown', 'calculation'],
    window_type: 'ROLLING_6M',
    unit: 'COUNT',
    has_components: true,
    has_transactions: false,
  },

  'lender.factors.debt_management': {
    metric_key: 'factor_debt_management',
    label: 'Debt Management',
    description: 'Factor score based on DTI and debt reduction trends',
    min_disclosure_level: 'LENDER',
    source_types: ['INTERNAL_DERIVED', 'PLAID_LIABILITIES'],
    drilldown_tabs: ['summary', 'breakdown', 'calculation'],
    window_type: 'ROLLING_6M',
    unit: 'COUNT',
    has_components: true,
    has_transactions: false,
  },

  'lender.factors.credit_history': {
    metric_key: 'factor_credit_history',
    label: 'Credit History',
    description: 'Factor score based on bureau data and payment history',
    min_disclosure_level: 'LENDER',
    source_types: ['BUREAU_CREDIT'],
    drilldown_tabs: ['summary', 'breakdown', 'calculation', 'sources'],
    window_type: 'AS_OF',
    unit: 'COUNT',
    has_components: true,
    has_transactions: false,
  },

  'lender.factors.financial_stress': {
    metric_key: 'factor_financial_stress',
    label: 'Financial Stress',
    description: 'Factor score based on cash flow volatility and overdrafts',
    min_disclosure_level: 'LENDER',
    source_types: ['INTERNAL_DERIVED', 'PLAID_TRANSACTIONS', 'PLAID_BALANCES'],
    drilldown_tabs: ['summary', 'breakdown', 'calculation'],
    window_type: 'ROLLING_6M',
    unit: 'COUNT',
    has_components: true,
    has_transactions: false,
  },

  // ---------------------------------------------------------------------------
  // VERASCORE METRICS (4 metrics)
  // ---------------------------------------------------------------------------

  // BUG-UI-032: VeraScore should have Calculation tab showing weighted sum formula
  // BUG-UI-068: Added Sources tab to show contributing factors
  'lender.verascore.current': {
    metric_key: 'verascore_current',
    label: 'Current VeraScore',
    description: 'Weighted combination of all 6 factor scores',
    min_disclosure_level: 'MEMBER',  // Members can see their score
    source_types: ['INTERNAL_DERIVED'],
    drilldown_tabs: ['summary', 'breakdown', 'calculation', 'sources'],  // BUG-UI-068: Added sources tab
    window_type: 'MONTH',
    unit: 'COUNT',
    has_components: true,  // Factor breakdown
    has_transactions: false,
  },

  'lender.verascore.six_month_avg': {
    metric_key: 'verascore_6m_avg',
    label: '6-Month Average',
    description: 'Rolling 6-month weighted average of VeraScore',
    min_disclosure_level: 'LENDER',
    source_types: ['INTERNAL_DERIVED'],
    drilldown_tabs: ['summary', 'calculation'],
    window_type: 'ROLLING_6M',
    unit: 'COUNT',
    has_components: false,
    has_transactions: false,
  },

  // ---------------------------------------------------------------------------
  // FACTOR CARDS (mapped to actual factor keys used in MemberCard)
  // These provide click-to-explain for the factor score cards
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // FACTOR CARDS - Use DATABASE entity names (algo_run_evidence.entity_name)
  // These map UI field paths to actual database column values
  // ---------------------------------------------------------------------------

  'lender.factors.dti': {
    metric_key: 'debt_to_income_factor',  // Maps to algo_run_evidence.entity_name
    label: 'Debt-to-Income',
    description: 'Factor score based on DTI ratio (min debt payments / gross income)',
    min_disclosure_level: 'LENDER',
    source_types: ['INTERNAL_DERIVED', 'PLAID_LIABILITIES'],
    drilldown_tabs: ['summary', 'breakdown', 'calculation'],
    window_type: 'MONTH',
    unit: 'COUNT',
    has_components: true,
    has_transactions: false,
  },

  // BUG-UI-040: Renamed from "Cash on Hand" to "Liquid Reserves" to match backend entity
  // The underlying factor is liquid_reserves_factor, not a simple cash balance
  'lender.factors.cash_on_hand': {
    metric_key: 'liquid_reserves_factor',  // Maps to algo_run_evidence.entity_name
    label: 'Liquid Reserves',
    description: 'Factor score based on liquid reserves (months of expenses covered)',
    min_disclosure_level: 'LENDER',
    source_types: ['INTERNAL_DERIVED', 'PLAID_BALANCES'],
    drilldown_tabs: ['summary', 'breakdown', 'calculation'],
    window_type: 'MONTH',
    unit: 'COUNT',
    has_components: true,
    has_transactions: false,
  },

  'lender.factors.spending': {
    metric_key: 'cash_flow_factor',  // Maps to algo_run_evidence.entity_name
    label: 'Spending',
    description: 'Factor score based on spending patterns and budget adherence',
    min_disclosure_level: 'LENDER',
    source_types: ['INTERNAL_DERIVED', 'PLAID_TRANSACTIONS'],
    drilldown_tabs: ['summary', 'breakdown', 'calculation'],
    window_type: 'MONTH',
    unit: 'COUNT',
    has_components: true,
    has_transactions: false,
  },

  'lender.factors.savings': {
    metric_key: 'savings_factor',  // Maps to algo_run_evidence.entity_name
    label: 'Savings',
    description: 'Factor score based on savings rate and consistency',
    min_disclosure_level: 'LENDER',
    source_types: ['INTERNAL_DERIVED', 'PLAID_TRANSACTIONS'],
    drilldown_tabs: ['summary', 'breakdown', 'calculation'],
    window_type: 'MONTH',
    unit: 'COUNT',
    has_components: true,
    has_transactions: false,
  },

  'lender.factors.debt': {
    metric_key: 'debt_management_factor',  // Maps to algo_run_evidence.entity_name
    label: 'Debt Management',
    description: 'Factor score based on debt levels and reduction trends',
    min_disclosure_level: 'LENDER',
    source_types: ['INTERNAL_DERIVED', 'PLAID_LIABILITIES'],
    drilldown_tabs: ['summary', 'breakdown', 'calculation'],
    window_type: 'MONTH',
    unit: 'COUNT',
    has_components: true,
    has_transactions: false,
  },

  'lender.factors.payment_history': {
    metric_key: 'payment_history_factor',  // Maps to algo_run_evidence.entity_name
    label: 'Payment History',
    description: 'Factor score based on on-time payment consistency',
    min_disclosure_level: 'LENDER',
    source_types: ['INTERNAL_DERIVED', 'BUREAU_CREDIT'],
    drilldown_tabs: ['summary', 'breakdown', 'calculation'],
    window_type: 'ROLLING_12M',
    unit: 'COUNT',
    has_components: true,
    has_transactions: false,
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get evidence config for a UI field path.
 * Returns undefined if not found (should never happen if registry is complete).
 */
export function getEvidenceConfig(uiFieldPath: string): UiEvidenceConfig | undefined {
  return UI_EVIDENCE_MAP[uiFieldPath];
}

/**
 * Get all metric keys that require a specific source type.
 */
export function getMetricsBySource(sourceType: SourceType): MetricKey[] {
  return Object.values(UI_EVIDENCE_MAP)
    .filter(config => config.source_types.includes(sourceType))
    .map(config => config.metric_key);
}

/**
 * Check if a user can view a specific metric based on their disclosure level.
 */
export function canViewMetric(
  uiFieldPath: string,
  userLevel: DisclosureLevel
): boolean {
  const config = UI_EVIDENCE_MAP[uiFieldPath];
  if (!config) return false;

  const levelOrder: DisclosureLevel[] = ['MEMBER', 'LENDER', 'AUDITOR'];
  const userLevelIndex = levelOrder.indexOf(userLevel);
  const requiredLevelIndex = levelOrder.indexOf(config.min_disclosure_level);

  return userLevelIndex >= requiredLevelIndex;
}

/**
 * Get visible tabs for a metric based on disclosure level.
 *
 * TODO: RBAC - Re-enable tiered access when role-based security is implemented
 * For now, all levels see all tabs during development/testing
 */
export function getVisibleTabs(
  uiFieldPath: string,
  userLevel: DisclosureLevel
): DrilldownTab[] {
  const config = UI_EVIDENCE_MAP[uiFieldPath];
  if (!config) return ['summary'];

  const allTabs = config.drilldown_tabs;

  // TODO: RBAC - Re-enable these restrictions when role-based security is implemented:
  // if (userLevel === 'MEMBER') {
  //   return ['summary'];
  // }
  // if (userLevel === 'LENDER') {
  //   return allTabs.filter(tab => tab === 'summary' || tab === 'breakdown');
  // }

  // TEMPORARY: All levels see all tabs until RBAC is implemented
  return allTabs;
}

